import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import { formatMoney } from "@bw/shared/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useCanReadMoney } from "@/hooks/use-can-read-money";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  confirmCheckin,
  getCheckinDetail,
  getTodayStats,
  listPendingArrivals,
  listRecentCheckins,
  searchPlayers,
  type CheckinDetail,
  type PendingArrival,
  type PlayerSearchResult,
  type RecentCheckin,
  type TodayStats,
} from "@/libs/frontDeskApi";
import { createZonedTimeFormatter } from "@/libs/zonedDateTime";

import { KioskRail } from "./kiosk/kiosk-rail";
import { KioskStatusChip } from "./kiosk/kiosk-status-chip";
import { useKioskFeed } from "./kiosk/use-kiosk-feed";

const PLACEHOLDER = "—";
const visuallyHidden = {
  position: "absolute",
  width: "1px",
  height: "1px",
  p: 0,
  m: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;
const STATS_ERROR = "Today’s arrival summary is unavailable. Try again.";
const PENDING_ERROR = "Pending arrivals are unavailable. Try again.";
const RECENT_ERROR = "Recent check-ins are unavailable. Try again.";
const SEARCH_ERROR = "Player search is unavailable. Try again.";

function safePersonName(
  displayName: string | null | undefined,
  userId: string | null | undefined,
  fallback = "Player",
): string {
  const name = displayName?.trim();
  return name && name !== userId?.trim() ? name : fallback;
}

function humanize(value: string | null | undefined): string {
  if (!value) return PLACEHOLDER;
  const words = value.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function formatArrivalTime(
  value: string | null | undefined,
  formatter: Intl.DateTimeFormat,
): string {
  if (!value) return PLACEHOLDER;
  const instant = new Date(value);
  return Number.isNaN(instant.getTime())
    ? PLACEHOLDER
    : formatter.format(instant);
}

export function ArrivalsConsole() {
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();

  if (facilityLoading || !facilityId) {
    return (
      <Box
        role="status"
        aria-live="polite"
        sx={{
          minHeight: "40vh",
          display: "grid",
          placeItems: "center",
          px: { xs: 2, lg: 3 },
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading arrivals…
        </Typography>
      </Box>
    );
  }

  const timeZone = facility?.timezone || "America/Los_Angeles";

  return (
    <ArrivalsFacilityConsole
      key={`${facilityId}:${timeZone}`}
      facilityId={facilityId}
      facilityName={facility?.name ?? "Selected facility"}
      timeZone={timeZone}
    />
  );
}

function ArrivalsFacilityConsole({
  facilityId,
  facilityName,
  timeZone,
}: {
  facilityId: string;
  facilityName: string;
  timeZone: string;
}) {
  const canReadMoney = useCanReadMoney();
  const kiosk = useKioskFeed(facilityId);
  const timeFormatter = useMemo(
    () => createZonedTimeFormatter(timeZone),
    [timeZone],
  );
  const mounted = useRef(true);
  const pendingGeneration = useRef(0);

  const [stats, setStats] = useState<TodayStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [pending, setPending] = useState<PendingArrival[]>([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingError, setPendingError] = useState<string | null>(null);

  const [recent, setRecent] = useState<RecentCheckin[]>([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [recentError, setRecentError] = useState<string | null>(null);

  const [confirming, setConfirming] = useState<string | null>(null);
  const confirmingRef = useRef(false);
  const confirmedIdsRef = useRef(new Set<string>());
  const awaitingRefreshRef = useRef<string | null>(null);
  const [confirmedAwaitingRefresh, setConfirmedAwaitingRefresh] = useState<
    string | null
  >(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<CheckinDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailPaymentMethod, setDetailPaymentMethod] = useState("");
  const detailGeneration = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pendingGeneration.current += 1;
      detailGeneration.current += 1;
    };
  }, []);

  const loadPending = useCallback(async (): Promise<boolean> => {
    const generation = ++pendingGeneration.current;
    setPendingLoading(true);
    setPendingError(null);
    try {
      const rows = await listPendingArrivals(facilityId);
      if (!mounted.current || generation !== pendingGeneration.current) {
        return false;
      }
      setPending(rows);
      const awaitingId = awaitingRefreshRef.current;
      if (awaitingId && !rows.some((row) => row.checkin_id === awaitingId)) {
        awaitingRefreshRef.current = null;
        setConfirmedAwaitingRefresh(null);
        setConfirmError(null);
      }
      return true;
    } catch {
      if (mounted.current && generation === pendingGeneration.current) {
        setPendingError(PENDING_ERROR);
      }
      return false;
    } finally {
      if (mounted.current && generation === pendingGeneration.current) {
        setPendingLoading(false);
      }
    }
  }, [facilityId]);

  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    setStatsError(null);
    void getTodayStats(facilityId)
      .then((value) => {
        if (!cancelled) setStats(value);
      })
      .catch(() => {
        if (!cancelled) setStatsError(STATS_ERROR);
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });

    void loadPending();

    setRecentLoading(true);
    setRecentError(null);
    void listRecentCheckins(facilityId)
      .then((rows) => {
        if (!cancelled) setRecent(rows);
      })
      .catch(() => {
        if (!cancelled) setRecentError(RECENT_ERROR);
      })
      .finally(() => {
        if (!cancelled) setRecentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [facilityId, loadPending]);

  const handleConfirm = useCallback(
    async (checkinId: string, paymentMethod?: string): Promise<boolean> => {
      if (
        confirmingRef.current ||
        awaitingRefreshRef.current !== null ||
        confirmedIdsRef.current.has(checkinId)
      ) {
        return false;
      }
      confirmingRef.current = true;
      setConfirming(checkinId);
      setConfirmError(null);
      try {
        await confirmCheckin(
          checkinId,
          paymentMethod ? { payment_method: paymentMethod } : {},
        );
      } catch {
        if (mounted.current) {
          setConfirmError("Failed to confirm arrival.");
          setConfirming(null);
        }
        confirmingRef.current = false;
        return false;
      }

      if (!mounted.current) {
        confirmingRef.current = false;
        return false;
      }

      confirmedIdsRef.current.add(checkinId);
      awaitingRefreshRef.current = checkinId;
      setConfirmedAwaitingRefresh(checkinId);
      const refreshed = await loadPending();
      if (mounted.current) {
        if (!refreshed) {
          setPendingError(null);
          setConfirmError(
            "Arrival was confirmed, but pending arrivals could not be refreshed.",
          );
        } else if (awaitingRefreshRef.current === checkinId) {
          setConfirmError(
            "Arrival was confirmed, but it still appears pending. Refresh pending arrivals before continuing.",
          );
        }
        setConfirming(null);
        confirmingRef.current = false;
      }
      return refreshed && awaitingRefreshRef.current !== checkinId;
    },
    [loadPending],
  );

  const retryPendingArrivals = useCallback(async (): Promise<void> => {
    const awaitingId = awaitingRefreshRef.current;
    if (!awaitingId) return;

    const refreshed = await loadPending();
    if (!mounted.current) return;

    if (!refreshed) {
      setPendingError(null);
      setConfirmError(
        "Arrival was confirmed, but pending arrivals could not be refreshed.",
      );
    } else if (awaitingRefreshRef.current === awaitingId) {
      setConfirmError(
        "Arrival was confirmed, but it still appears pending. Refresh pending arrivals before continuing.",
      );
    }
  }, [loadPending]);

  const openDetail = useCallback(
    async (checkinId: string): Promise<void> => {
      const generation = ++detailGeneration.current;
      setDetailOpen(true);
      setDetail(null);
      setDetailError(null);
      setDetailPaymentMethod("");
      setDetailLoading(true);
      try {
        const value = await getCheckinDetail(checkinId);
        if (mounted.current && generation === detailGeneration.current) {
          if (value.facility_id !== facilityId) {
            setDetailError("Arrival detail is unavailable. Try again.");
          } else {
            setDetail(value);
          }
        }
      } catch {
        if (mounted.current && generation === detailGeneration.current) {
          setDetailError("Arrival detail is unavailable. Try again.");
        }
      } finally {
        if (mounted.current && generation === detailGeneration.current) {
          setDetailLoading(false);
        }
      }
    },
    [facilityId],
  );

  const closeDetail = () => {
    detailGeneration.current += 1;
    setDetailOpen(false);
    setDetail(null);
    setDetailError(null);
    setDetailPaymentMethod("");
    setDetailLoading(false);
  };

  const handleConfirmFromDetail = async () => {
    if (!detail) return;
    const confirmed = await handleConfirm(
      detail.checkin_id,
      detailPaymentMethod || undefined,
    );
    if (confirmed) closeDetail();
  };

  const valueFromStats = (
    read: (value: TodayStats) => string | number | null | undefined,
  ): string => {
    if (statsLoading || !stats) return PLACEHOLDER;
    const value = read(stats);
    return value === null || value === undefined ? PLACEHOLDER : String(value);
  };
  const courts =
    !statsLoading && stats?.courts_free != null && stats.courts_total != null
      ? `${stats.courts_free} / ${stats.courts_total}`
      : PLACEHOLDER;
  const revenue =
    !statsLoading && stats?.today_revenue != null && stats.currency
      ? formatMoney(stats.today_revenue, stats.currency)
      : PLACEHOLDER;
  const summary = [
    {
      label: "Checked in",
      value: valueFromStats((value) => value.checkin_count),
    },
    { label: "In queue", value: valueFromStats((value) => value.queue_count) },
    { label: "Courts free", value: courts },
    // Money tiles only for internal reads or org finance read.
    ...(canReadMoney ? [{ label: "Revenue today", value: revenue }] : []),
  ];

  return (
    <Stack spacing={1.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      {/* No visible page title (the shell names the page); the h1 stays for
          screen readers and names the facility. */}
      <Typography component="h1" sx={visuallyHidden}>
        Arrivals
      </Typography>
      <Typography sx={visuallyHidden}>{facilityName}</Typography>
      <Box
        aria-label="Front desk tools"
        role="toolbar"
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        {kiosk.forbidden ? null : (
          <KioskStatusChip
            facilityId={facilityId}
            devices={kiosk.devices}
            loading={kiosk.loading}
          />
        )}
        <Button
          component={RouterLink}
          to="/front-desk/devices"
          variant="outlined"
          color="inherit"
          size="small"
          startIcon={<DevicesOutlinedIcon fontSize="small" />}
          sx={{
            ml: "auto",
            minHeight: { xs: 44, md: 32 },
            borderColor: "divider",
          }}
        >
          Devices
        </Button>
      </Box>

      {statsError ? <Alert severity="error">{statsError}</Alert> : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: kiosk.forbidden ? "minmax(0, 1fr)" : "minmax(0, 1fr) 360px",
          },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Stack spacing={2} sx={{ minWidth: 0, order: { xs: 2, lg: 1 } }}>
          <Paper
            component="section"
            aria-label="Today summary"
            variant="outlined"
            sx={{ p: { xs: 1.5, sm: 2 } }}
          >
            <Box
              component="dl"
              sx={{
                m: 0,
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  md: `repeat(${summary.length}, minmax(0, 1fr))`,
                },
                gap: 2,
              }}
            >
              {summary.map((item) => (
                <Box key={item.label} sx={{ minWidth: 0 }}>
                  <Typography
                    component="dt"
                    variant="body2"
                    color="text.secondary"
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    component="dd"
                    variant="h6"
                    sx={{
                      m: 0,
                      fontWeight: 700,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {statsLoading ? <Skeleton width={64} /> : item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>

          <Paper
            component="section"
            aria-labelledby="pending-arrivals-title"
            variant="outlined"
            sx={{ overflow: "hidden" }}
          >
            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography
                id="pending-arrivals-title"
                component="h2"
                variant="h6"
              >
                Pending arrivals
              </Typography>
            </Box>
            <Divider />
            <Stack spacing={1.5} sx={{ p: { xs: 1.5, sm: 2 } }}>
              {confirmError ? (
                <Alert
                  severity={confirmedAwaitingRefresh ? "warning" : "error"}
                  action={
                    confirmedAwaitingRefresh ? (
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => void retryPendingArrivals()}
                        sx={{ minHeight: 44 }}
                      >
                        Retry pending arrivals
                      </Button>
                    ) : undefined
                  }
                >
                  {confirmError}
                </Alert>
              ) : null}
              {pendingError ? (
                <Alert severity="error">{pendingError}</Alert>
              ) : pendingLoading ? (
                <Stack
                  spacing={1}
                  role="status"
                  aria-label="Loading pending arrivals"
                >
                  <Skeleton height={56} />
                  <Skeleton height={56} />
                </Stack>
              ) : pending.length === 0 ? (
                <Typography
                  role="status"
                  color="text.secondary"
                  sx={{ py: 4, textAlign: "center" }}
                >
                  No pending arrivals.
                </Typography>
              ) : (
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
                  {pending.map((row, index) => (
                    <ArrivalRow
                      key={row.checkin_id}
                      row={row}
                      timeFormatter={timeFormatter}
                      confirming={confirming === row.checkin_id}
                      disabled={confirming !== null}
                      locked={confirmedAwaitingRefresh !== null}
                      divided={index > 0}
                      onConfirm={() => void handleConfirm(row.checkin_id)}
                      onOpenDetail={() => void openDetail(row.checkin_id)}
                    />
                  ))}
                </Box>
              )}
            </Stack>
          </Paper>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                lg: "repeat(2, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            <PlayerSearchPanel facilityId={facilityId} />
            <RecentCheckinsPanel
              rows={recent}
              loading={recentLoading}
              error={recentError}
              timeFormatter={timeFormatter}
            />
          </Box>
        </Stack>
        <Box sx={{ order: { xs: 1, lg: 2 }, minWidth: 0 }}>
          <KioskRail facilityId={facilityId} timeZone={timeZone} feed={kiosk} />
        </Box>
      </Box>

      <Dialog open={detailOpen} onClose={closeDetail} fullWidth maxWidth="sm">
        <DialogTitle>
          {detail
            ? safePersonName(
                detail.display_name,
                detail.user_id,
                "Arrival detail",
              )
            : "Arrival detail"}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Stack
              spacing={1}
              role="status"
              aria-label="Loading arrival detail"
            >
              <Skeleton height={40} />
              <Skeleton height={40} />
            </Stack>
          ) : detailError ? (
            <Alert severity="error">{detailError}</Alert>
          ) : detail ? (
            <Stack spacing={2}>
              <Box component="dl" sx={{ m: 0 }}>
                <DetailRow
                  label="Court"
                  value={detail.court_name?.trim() || PLACEHOLDER}
                />
                <DetailRow
                  label="Payment status"
                  value={humanize(detail.payment_status)}
                />
                {detail.membership_status ? (
                  <DetailRow
                    label="Membership"
                    value={
                      detail.membership_status === "active"
                        ? "Member"
                        : humanize(detail.membership_status)
                    }
                  />
                ) : null}
              </Box>
              <FormControl fullWidth>
                <InputLabel id="detail-payment-method-label">
                  Payment method (optional)
                </InputLabel>
                <Select
                  labelId="detail-payment-method-label"
                  id="detail-payment-method"
                  value={detailPaymentMethod}
                  label="Payment method (optional)"
                  onChange={(event) =>
                    setDetailPaymentMethod(event.target.value)
                  }
                >
                  <MenuItem value="">Not recorded</MenuItem>
                  <MenuItem value="single">Single drop-in</MenuItem>
                  <MenuItem value="package">Package</MenuItem>
                  <MenuItem value="membership">Membership</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDetail} sx={{ minHeight: 44 }}>
            Close
          </Button>
          {confirmedAwaitingRefresh ? (
            <Button
              onClick={() => void retryPendingArrivals()}
              sx={{ minHeight: 44 }}
            >
              Retry pending arrivals
            </Button>
          ) : null}
          {detail ? (
            <Button
              variant="contained"
              onClick={() => void handleConfirmFromDetail()}
              disabled={
                confirming !== null ||
                confirmedAwaitingRefresh !== null ||
                confirmedIdsRef.current.has(detail.checkin_id)
              }
              sx={{ minHeight: 44 }}
            >
              {confirming === detail.checkin_id ? "Confirming…" : "Confirm"}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function ArrivalRow({
  row,
  timeFormatter,
  confirming,
  disabled,
  locked,
  divided,
  onConfirm,
  onOpenDetail,
}: {
  row: PendingArrival;
  timeFormatter: Intl.DateTimeFormat;
  confirming: boolean;
  disabled: boolean;
  locked: boolean;
  divided: boolean;
  onConfirm: () => void;
  onOpenDetail: () => void;
}) {
  const name = safePersonName(row.display_name, row.user_id);
  return (
    <Box
      component="li"
      data-testid="arrival-row"
      sx={{
        py: 1.5,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1.5,
        borderTop: divided ? 1 : 0,
        borderColor: "divider",
      }}
    >
      <Box sx={{ minWidth: 0, flex: "1 1 240px" }}>
        <Button
          color="inherit"
          onClick={onOpenDetail}
          aria-label={`View ${name}`}
          sx={{
            minHeight: 44,
            px: 0,
            justifyContent: "flex-start",
            fontWeight: 700,
          }}
        >
          {name}
        </Button>
        <Stack
          direction="row"
          useFlexGap
          spacing={1}
          sx={{ flexWrap: "wrap", alignItems: "center" }}
        >
          <Chip
            size="small"
            label={row.booking_type === "booking" ? "Booking" : "Walk-in"}
            color={row.booking_type === "booking" ? "primary" : "default"}
          />
          {row.start_time ? (
            <Typography variant="caption" color="text.secondary">
              {formatArrivalTime(row.start_time, timeFormatter)}
            </Typography>
          ) : null}
          {row.court_name ? (
            <Typography variant="caption" color="text.secondary">
              {row.court_name}
            </Typography>
          ) : null}
        </Stack>
      </Box>
      <Button
        variant="contained"
        onClick={onConfirm}
        disabled={locked}
        aria-disabled={disabled || locked || undefined}
        aria-label={`Confirm ${name}`}
        sx={{
          minHeight: 44,
          ...(disabled || locked
            ? { opacity: 0.6, cursor: "default", boxShadow: "none" }
            : null),
        }}
      >
        {confirming ? "Confirming…" : "Confirm"}
      </Button>
    </Box>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        py: 1,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Typography component="dt" color="text.secondary">
        {label}
      </Typography>
      <Typography
        component="dd"
        sx={{ m: 0, fontWeight: 600, textAlign: "right" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function RecentCheckinsPanel({
  rows,
  loading,
  error,
  timeFormatter,
}: {
  rows: RecentCheckin[];
  loading: boolean;
  error: string | null;
  timeFormatter: Intl.DateTimeFormat;
}) {
  return (
    <Paper
      component="section"
      aria-labelledby="recent-checkins-title"
      variant="outlined"
      sx={{ p: { xs: 1.5, sm: 2 }, minWidth: 0 }}
    >
      <Typography id="recent-checkins-title" component="h2" variant="h6">
        Recent check-ins
      </Typography>
      <Box sx={{ mt: 1.5 }}>
        {loading ? (
          <Skeleton
            height={48}
            role="status"
            aria-label="Loading recent check-ins"
          />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : rows.length === 0 ? (
          <Typography
            role="status"
            color="text.secondary"
            sx={{ py: 3, textAlign: "center" }}
          >
            No check-ins yet today.
          </Typography>
        ) : (
          <Stack component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
            {rows.map((row, index) => (
              <Box
                component="li"
                key={row.checkin_id}
                sx={{
                  py: 1.25,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  borderTop: index > 0 ? 1 : 0,
                  borderColor: "divider",
                }}
              >
                <Typography
                  sx={{
                    minWidth: 0,
                    overflowWrap: "anywhere",
                    fontWeight: 600,
                  }}
                >
                  {safePersonName(
                    row.display_name,
                    row.user_id,
                    "Checked in player",
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {formatArrivalTime(row.checked_in_at, timeFormatter)}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

function PlayerSearchPanel({ facilityId }: { facilityId: string }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PlayerSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const searchGeneration = useRef(0);
  const trimmed = query.trim();

  useEffect(() => {
    const generation = ++searchGeneration.current;
    if (trimmed.length < 2) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      return;
    }

    let cancelled = false;
    setResults([]);
    setSearching(true);
    setSearchError(null);
    const handle = window.setTimeout(() => {
      void searchPlayers(facilityId, trimmed)
        .then((rows) => {
          if (!cancelled && generation === searchGeneration.current)
            setResults(rows);
        })
        .catch(() => {
          if (!cancelled && generation === searchGeneration.current) {
            setSearchError(SEARCH_ERROR);
          }
        })
        .finally(() => {
          if (!cancelled && generation === searchGeneration.current)
            setSearching(false);
        });
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [facilityId, trimmed]);

  return (
    <Paper
      component="section"
      aria-labelledby="find-player-title"
      variant="outlined"
      sx={{ p: { xs: 1.5, sm: 2 }, minWidth: 0 }}
    >
      <Typography id="find-player-title" component="h2" variant="h6">
        Find a player
      </Typography>
      <TextField
        type="search"
        fullWidth
        label="Search players"
        placeholder="Name or exact customer ID"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        slotProps={{ htmlInput: { role: "searchbox" } }}
        sx={{ mt: 1.5 }}
      />
      <Box sx={{ mt: 1.5 }}>
        {searchError ? <Alert severity="error">{searchError}</Alert> : null}
        {searching ? (
          <Skeleton height={48} role="status" aria-label="Searching players" />
        ) : results.length > 0 ? (
          <Stack component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
            {results.map((player, index) => {
              const name = safePersonName(player.display_name, player.user_id);
              return (
                <Box
                  component="li"
                  key={player.user_id}
                  sx={{
                    py: 1.25,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                    borderTop: index > 0 ? 1 : 0,
                    borderColor: "divider",
                  }}
                >
                  <Avatar
                    src={player.avatar_url ?? undefined}
                    alt=""
                    sx={{ width: 36, height: 36 }}
                  >
                    {name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography
                    sx={{
                      minWidth: 0,
                      flex: "1 1 160px",
                      overflowWrap: "anywhere",
                      fontWeight: 600,
                    }}
                  >
                    {name}
                  </Typography>
                  <SearchAssociation association={player.association} />
                </Box>
              );
            })}
          </Stack>
        ) : trimmed.length >= 2 && !searchError ? (
          <Typography
            role="status"
            color="text.secondary"
            sx={{ py: 2, textAlign: "center" }}
          >
            No players match.
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
}

function SearchAssociation({
  association,
}: {
  association: PlayerSearchResult["association"];
}) {
  if (association === "booking_and_checkin") {
    return (
      <Chip
        size="small"
        color="primary"
        label="Booking and previous check-in"
      />
    );
  }
  if (association === "booking") {
    return <Chip size="small" color="primary" label="Booking customer" />;
  }
  if (association === "checkin") {
    return <Chip size="small" label="Previous check-in" />;
  }
  if (association === "exact_id") {
    return <Chip size="small" label="Exact ID match" />;
  }
  return null;
}
